import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Notification } from '../data/types';
import './cloche.css';
import { ilYA, libelleGenre, lienNotification } from './modele-notification';
import { useNotifications } from './useNotifications';

type ClocheProps = { monMembreId: string | null };

/**
 * La cloche : ce qui est arrivé pour moi, du plus récent au plus ancien.
 * Un tap ouvre l'écran concerné et marque la ligne lue. Rien d'autre.
 */
export function Cloche({ monMembreId }: ClocheProps) {
  const { liste, nonLues, chargement, erreur, lire, toutLire } = useNotifications(monMembreId);
  const [ouverte, setOuverte] = useState(false);
  const cadre = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ouverte) return;
    function auClic(evenement: MouseEvent) {
      if (!cadre.current?.contains(evenement.target as Node)) setOuverte(false);
    }
    function auClavier(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') setOuverte(false);
    }
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouverte]);

  function ouvrir(notification: Notification) {
    setOuverte(false);
    void lire(notification.id);
    void navigate(lienNotification(notification));
  }

  return (
    <div className="cloche" ref={cadre}>
      <button
        type="button"
        className="cloche__bouton"
        aria-expanded={ouverte}
        aria-label={nonLues > 0 ? `Notifications, ${nonLues} non lues` : 'Notifications'}
        onClick={() => setOuverte((etat) => !etat)}
      >
        <span aria-hidden="true">🔔</span>
        {nonLues > 0 && (
          <span className="cloche__pastille" aria-hidden="true">
            {nonLues > 99 ? '99+' : nonLues}
          </span>
        )}
      </button>

      {ouverte && (
        <div className="cloche__panneau" role="dialog" aria-label="Notifications">
          <header className="cloche__entete">
            <p className="cloche__titre">Notifications</p>
            {nonLues > 0 && (
              <button type="button" className="cloche__tout-lire" onClick={() => void toutLire()}>
                Tout marquer lu
              </button>
            )}
          </header>

          {chargement && <p className="cloche__etat">Chargement.</p>}
          {erreur && (
            <p className="cloche__etat" role="alert">
              {erreur}
            </p>
          )}
          {!chargement && !erreur && liste.length === 0 && (
            <p className="cloche__etat">Rien de neuf.</p>
          )}

          <ul className="cloche__liste">
            {liste.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  className={
                    notification.read_at === null ? 'cloche__ligne non-lue' : 'cloche__ligne'
                  }
                  onClick={() => ouvrir(notification)}
                >
                  <span className="cloche__genre">{libelleGenre(notification.kind)}</span>
                  <span className="cloche__objet">{notification.title}</span>
                  {notification.body && <span className="cloche__corps">{notification.body}</span>}
                  <span className="cloche__quand">{ilYA(notification.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
